import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { FormEventHandler, useCallback, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePostTranslationsImportXml } from '../../../../api/translations/translations';
import { usePostUtilsDownload } from '../../../../api/utils/utils';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import yup from '../../../../utils/custom-yup';
import { executeDownload } from '../../../../utils/download';
import FileDrop from '../../../atoms/FileDrop';
import ErrorModal from '../../../modules/ErrorModal';
import Modal from '../../../modules/Modal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';

interface Props {
  active: boolean;
  productionId: number;
  onClose: () => void;
}

/**
 * 翻訳タブのインポートダイアログ
 * @param param0
 * @returns
 */
function ProductionTranslationsImportModal({ active, productionId, onClose }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Form定義
  const schema = yup.object({
    comment: yup
      .string()
      .nullable()
      .transform((v) => (v ? (v as string) : undefined)),
    importFiles: yup.array().required(),
  });

  const {
    handleSubmit,
    setValue,
    register,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [displayOkModal, setDisplayOkModal] = useState(0); // エラーダイアログ

  // #region インポート処理API定義

  const options = useDefaultOptions(language);
  const {
    mutateAsync: importXmlAsync,
    error: importXmlError,
    isLoading: importXmlIsLoading,
  } = usePostTranslationsImportXml(options);
  const [displayImportXmlError, setDisplayImportXmlError] = useState(false);

  // インポート処理エラー（importXmlErrorに変化があると起動）
  useEffect(() => {
    setDisplayImportXmlError(importXmlError != null);
    setIsSubmitting(false);
  }, [importXmlError]);

  // #endregion

  // #region インポート処理エラーログAPI定義

  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: _downloadAsync,
    isError: downloadIsError,
    error: downloadError,
  } = usePostUtilsDownload<AxiosError<Blob>>(optionsForBinary);
  // #endregion

  // キャンセル処理
  const onCancelClicked = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  // インポート実行
  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      setIsSubmitting(true);

      void handleSubmit(async (data: FieldValues) => {
        if (data.importFiles.length > 0) {
          const responseImport = await importXmlAsync({
            productionId,
            data: { comment: data.comment, 'files[]': data.importFiles },
          });

          if (responseImport.status === 200) {
            // apiの返値にエラーログのパスが返って来たらダウンロードする
            if (responseImport.data.result_file_info !== undefined && responseImport.data.result_file_info !== null) {
              // setDisplayOkModal(2);
              await _downloadAsync(
                { data: responseImport.data.result_file_info },
                {
                  onSuccess: (responseDL) => {
                    setDisplayOkModal(2);
                    executeDownload(responseDL);
                  },
                }
              );
            } else {
              setDisplayOkModal(1);
            }

            // reset();
            // onClose();
          }
        }
      })();
      // setIsSubmitting(false);
    },
    [handleSubmit, productionId, importXmlAsync, _downloadAsync, setDisplayOkModal]
  );

  return (
    <Modal active={active} additionalClassName="mtsp" displayCloseButton onClose={onCancelClicked}>
      <form onSubmit={onSubmit}>
        <fieldset disabled={isSubmitting} className="fieldset-reset">
          <div className="formarea1">
            <label htmlFor="">{t('添付ファイル')}</label>
            <div>
              <FileDrop id="importFiles" name="importFiles" setValue={setValue} />
              {errors.importFiles?.message != null && (
                <p className="form-error">{errors.importFiles.message.toString()}</p>
              )}
            </div>
          </div>
          <div className="formarea1">
            <label htmlFor="">{t('コメント')}</label>
            <textarea id="" rows={10} cols={33} {...register('comment')} />
          </div>
          <div className="btnfr btnfr2 mb0">
            <div>
              <button type="button" className="text-button" onClick={() => onCancelClicked()}>
                <span className="btn2 mr10 btnbg-g">
                  <span className="modal-close2">{t('キャンセル')}</span>
                </span>
              </button>
              <span>
                <input type="submit" id="" name="" value={t('インポート').toString()} className="btn1" />
              </span>{' '}
            </div>
          </div>
        </fieldset>
      </form>
      {importXmlIsLoading && <SpinnerModal />}
      {displayImportXmlError && (
        <ErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayImportXmlError(false);
            reset();
            onClose();
          }}
          error={importXmlError}
        />
      )}
      {displayOkModal > 0 && (
        <OkModal
          onClickOk={() => {
            setDisplayOkModal(0);
            setIsSubmitting(false);
            reset();
            onClose();
          }}
        >
          {displayOkModal === 1 && t('翻訳xmlをインポートしました。')}
          {displayOkModal === 2 && t('翻訳xmlにエラーがあります。インポート処理を中断しました。')}
        </OkModal>
      )}
    </Modal>
  );
}

export default ProductionTranslationsImportModal;
